import React from 'react';
import { AuditChange, AuditEvent } from '../../components/services/auditService';
import OutcomeBadge from './OutcomeBadge';
import { entityLabel } from './auditEntities';
import { actorDisplayName, describeAuditValue, formatAuditTimePrecise, getActorHomeTenantId } from './auditFormat';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="mb-5">
    <h4 className="mb-2 text-sm font-semibold text-gray-300">{title}</h4>
    {children}
  </section>
);

const Row = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 py-1 text-sm">
    <dt className="text-gray-400">{label}</dt>
    <dd className="break-all text-white">{value === undefined || value === null || value === '' ? '—' : value}</dd>
  </div>
);

const VALUE_CLASSES: Record<string, string> = {
  masked: 'italic text-amber-300',
  hashed: 'italic text-sky-300',
  empty: 'text-gray-500',
};

/** A field value; for an id field with a known record (`name`), the name with the id under it. */
export const AuditValue = ({ value, name }: { value: unknown; name?: string | null }) => {
  const display = describeAuditValue(value);
  if (name && display.kind === 'value') {
    return (
      <span className="break-all">
        <span className="text-white">{name}</span>
        <span className="block text-xs text-gray-500">{display.text}</span>
      </span>
    );
  }
  if (display.kind === 'truncated') {
    return (
      <span className="break-all">
        {display.text}
        <span className="ml-1 rounded border border-gray-600 px-1 text-xs text-gray-400">truncated</span>
      </span>
    );
  }
  return <span className={`break-all ${VALUE_CLASSES[display.kind] ?? ''}`}>{display.text}</span>;
};

const ChangeCard = ({ change }: { change: AuditChange }) => (
  <div className="mb-3 rounded border border-[#474646] p-3" data-testid="audit-change">
    <dl className="mb-2">
      <Row label="Operation" value={change.operation} />
      <Row label="Entity" value={`${entityLabel(change.entity)} (${change.entity})`} />
      {change.displayName && <Row label="Record name" value={change.displayName} />}
      <Row label="Key" value={change.key} />
      <Row label="Store" value={change.store} />
    </dl>
    {change.fieldsOmitted ? (
      <p className="text-sm italic text-gray-400">Details not captured</p>
    ) : change.fields.length === 0 ? (
      <p className="text-sm italic text-gray-400">No field values recorded</p>
    ) : (
      <table className="w-full table-fixed text-left text-sm">
        <thead>
          <tr className="text-gray-400">
            <th className="w-1/3 py-1 font-normal">Field</th>
            <th className="py-1 font-normal">Old</th>
            <th className="py-1 font-normal">New</th>
          </tr>
        </thead>
        <tbody>
          {change.fields.map((field, index) => (
            <tr key={`${field.name}:${index}`} className="border-t border-[#2a2a2a] align-top">
              <td className="break-all py-1 pr-2 text-gray-300">{field.name}</td>
              <td className="py-1 pr-2">
                <AuditValue value={field.old} name={field.oldName} />
              </td>
              <td className="py-1">
                <AuditValue value={field.new} name={field.newName} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

/** `actionName`: the customer-facing name of `event.action`, when known (GET auditlog/actions). */
const EventDetail = ({ event, actionName }: { event: AuditEvent; actionName?: string }) => {
  const actor = event.actor;
  const homeTenantId = getActorHomeTenantId(event);
  const changes = event.changes ?? [];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span className="text-lg font-semibold text-white" title={event.action}>
          {actionName || event.action}
        </span>
        {actionName && actionName !== event.action && <span className="text-xs text-gray-500">{event.action}</span>}
        <OutcomeBadge outcome={event.outcome} />
        <span className="text-sm text-gray-400">{formatAuditTimePrecise(event.timestampUtc)}</span>
      </div>

      <Section title="Summary">
        <dl>
          <Row label="Category" value={event.category} />
          <Row label="Service" value={event.service} />
          <Row label="Target tenant" value={event.targetTenantId ?? 'Global / none'} />
          {event.failureReason && <Row label="Failure reason" value={event.failureReason} />}
          <Row label="Correlation ID" value={event.correlationId} />
        </dl>
      </Section>

      <Section title="Actor">
        <dl>
          <Row label="Name" value={actorDisplayName(actor)} />
          <Row label="Email" value={actor?.email} />
          <Row label="Role" value={actor?.role} />
          <Row label="Home tenant" value={homeTenantId} />
          <Row label="IP address" value={actor?.ip} />
          <Row label="User agent" value={actor?.userAgent} />
        </dl>
      </Section>

      {event.http && (
        <Section title="Request">
          <dl>
            <Row label="Method" value={event.http.method} />
            <Row label="Route" value={event.http.route} />
            <Row label="Status" value={event.http.statusCode} />
          </dl>
        </Section>
      )}

      <Section title="Changes">
        {(event.truncated || event.omittedChangeCount > 0) && (
          <div role="note" className="mb-3 rounded border border-amber-700 bg-amber-900/20 p-2 text-sm text-amber-200">
            Some details of this event were dropped to stay within the size limit.
            {event.omittedChangeCount > 0 &&
              ` ${event.omittedChangeCount} change${event.omittedChangeCount === 1 ? ' was' : 's were'} not recorded.`}
          </div>
        )}
        {changes.length === 0 ? (
          <p className="text-sm italic text-gray-400">No data changes were recorded for this event.</p>
        ) : (
          changes.map((change, index) => <ChangeCard key={`${change.entity}:${change.key}:${index}`} change={change} />)
        )}
      </Section>
    </div>
  );
};

export default EventDetail;
